import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Orbit, Search, Sparkles, Rss, ArrowLeft, ArrowRight, Loader2, Link2, ExternalLink, Send, Brain, Users } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import ShareBar from "@/components/ShareBar";
import { toast } from "sonner";

const GOLD = "#c9a86a";
const BG = "#0b0e14";
const CARD = "#11151d";
const BORDER = "#232935";
const MUTED = "#a9a291";
const TEXT = "#e7e2d6";

// Turn recognised topic terms inside article text into clickable inline links.
function linkify(text, terms, onNav) {
  if (!text) return null;
  if (!terms?.length) return text;
  const escaped = terms.map((t) => t.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`\\b(${escaped.join("|")})\\b`, "gi");
  const slugByTerm = {};
  terms.forEach((t) => { slugByTerm[t.term.toLowerCase()] = t.slug; });
  const parts = text.split(re);
  const seen = new Set();
  return parts.map((part, i) => {
    const slug = slugByTerm[(part || "").toLowerCase()];
    if (slug && !seen.has(slug)) {
      seen.add(slug); // link the first occurrence of each topic only
      return (
        <button key={i} onClick={() => onNav(slug)} data-testid={`inline-link-${slug}`}
          className="underline decoration-dotted underline-offset-2 hover:opacity-80 transition-opacity" style={{ color: GOLD }}>
          {part}
        </button>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function NewsFeed({ news, onOpen }) {
  return (
    <div className="rounded-2xl p-5 border" style={{ backgroundColor: CARD, borderColor: BORDER }} data-testid="galacto-news">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>
        <Rss size={14} /> Galactic news feed
      </div>
      {news.length === 0 ? (
        <p className="text-sm" style={{ color: MUTED }}>No updates yet — be the first to add knowledge.</p>
      ) : (
        <div className="space-y-3">
          {news.map((n) => (
            <button key={n.id} data-testid={`news-${n.entry_slug}`} onClick={() => onOpen(n.entry_slug)}
              className="w-full text-left group">
              <div className="text-sm font-medium leading-snug group-hover:opacity-80 transition-opacity" style={{ color: TEXT }}>
                {n.title}
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: MUTED }}>
                <span className="capitalize" style={{ color: GOLD }}>{n.action}</span>
                {" · "}{n.contributor_name}{n.category ? ` · ${n.category}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Contribute({ onDone }) {
  const { user, setUser } = useApp();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) { toast.error("Sign in to contribute to Galactopedia."); return; }
    if (content.trim().length < 40) { toast.error("Add a bit more factual content (40+ characters)."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/galactopedia/push", { title: title.trim(), content: content.trim(), source_type: "manual" });
      if (typeof data.credits_remaining === "number" && setUser) setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      if (data.accepted) {
        toast.success(`Accepted into Galactopedia! You earned ${data.reward} credit. 🌌`);
        setTitle(""); setContent(""); setOpen(false);
        onDone?.(data.slug);
      } else {
        toast.error(`Gatekeeper: ${data.reason} (your ${data.refunded} credit hold was refunded).`);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-5 border" style={{ backgroundColor: CARD, borderColor: BORDER }} data-testid="galacto-contribute">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-2" style={{ color: GOLD }}>
        <Brain size={14} /> Add to the collective brain
      </div>
      <p className="text-sm" style={{ color: MUTED }}>
        Share verified, factual knowledge. Our AI Gatekeeper reviews it — accepted contributions earn you <b style={{ color: GOLD }}>+0.1 credit</b> and join the wiki.
      </p>
      {!open ? (
        <button data-testid="galacto-contribute-open" onClick={() => setOpen(true)}
          className="mt-3 px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5 font-medium" style={{ backgroundColor: GOLD, color: BG }}>
          <Send size={14} /> Contribute knowledge
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <input data-testid="galacto-contribute-title" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Topic title (e.g. Photosynthesis)"
            className="w-full rounded-lg px-3 py-2 text-sm outline-none border" style={{ backgroundColor: BG, borderColor: BORDER, color: TEXT }} />
          <textarea data-testid="galacto-contribute-content" value={content} onChange={(e) => setContent(e.target.value)} rows={5}
            placeholder="Write factual, encyclopedic knowledge about this topic…"
            className="w-full rounded-lg px-3 py-2 text-sm outline-none border resize-y" style={{ backgroundColor: BG, borderColor: BORDER, color: TEXT }} />
          <div className="flex items-center gap-2">
            <button data-testid="galacto-contribute-submit" onClick={submit} disabled={busy}
              className="px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5 font-medium disabled:opacity-60" style={{ backgroundColor: GOLD, color: BG }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit to Gatekeeper
            </button>
            <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-full text-sm border" style={{ borderColor: BORDER, color: MUTED }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Galactopedia() {
  const { user, setUser } = useApp();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState(params.get("q") || "");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [entry, setEntry] = useState(null);
  const [linkTerms, setLinkTerms] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [shareVec, setShareVec] = useState("");

  // Topic-specific monochrome + heart-red share vector for the open article (cached by slug).
  useEffect(() => {
    if (!entry?.title) { setShareVec(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/search/share", {
          query: entry.title,
          kind: "galactopedia",
          summary: (entry.summary || "").slice(0, 240),
          href: `/galactopedia?q=${encodeURIComponent(entry.slug || entry.title)}`,
        });
        if (!cancelled && data?.url) setShareVec(data.url);
      } catch { /* ShareBar falls back to page URL */ }
    })();
    return () => { cancelled = true; };
  }, [entry?.slug, entry?.title]);

  const loadNews = useCallback(async () => {
    try { const { data } = await api.get("/galactopedia/news", { params: { limit: 15 } }); setNews(data.news || []); } catch (_) {}
  }, []);

  const loadEntry = useCallback(async (slug) => {
    setLoading(true); setEntry(null);
    try {
      const { data } = await api.get(`/galactopedia/entry/${slug}`);
      setEntry(data.entry); setLinkTerms(data.link_terms || []);
      setParams({ t: slug }, { replace: false });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [setParams]);

  const runSearch = useCallback(async (q) => {
    const term = (q ?? query).trim();
    setSearched(true); setEntry(null);
    try {
      const { data } = await api.get("/galactopedia/search", { params: { q: term, limit: 24 } });
      setResults(data.results || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [query]);

  const generate = async () => {
    if (!user) { toast.error("Sign in to research a new topic."); return; }
    if (query.trim().length < 2) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/galactopedia/generate", { topic: query.trim() });
      if (data.charged && typeof data.credits_remaining === "number" && setUser) {
        setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      }
      toast.success(data.existed ? "Found it in Galactopedia." : `Researched and added to Galactopedia (−${data.charged} credit).`);
      loadNews();
      await loadEntry(data.entry.slug);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  useEffect(() => { api.get("/galactopedia/meta").then((r) => setMeta(r.data)).catch(() => {}); loadNews(); }, [loadNews]);

  // Sync from the URL (topic slug or search query).
  useEffect(() => {
    const t = params.get("t");
    const q = params.get("q");
    if (t) { loadEntry(t); }
    else if (q) { setQuery(q); runSearch(q); }
    else { setEntry(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submitSearch = (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setParams({ q: query.trim() }, { replace: false });
  };

  const goHome = () => { setEntry(null); setResults([]); setSearched(false); setQuery(""); setParams({}, { replace: false }); };

  const related = React.useMemo(() => {
    const seen = new Set(); const out = [];
    for (const t of linkTerms) { if (!seen.has(t.slug)) { seen.add(t.slug); out.push(t); } if (out.length >= 10) break; }
    return out;
  }, [linkTerms]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }} data-testid="galactopedia-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-24 pb-24" style={{ color: TEXT }}>
        {/* Hero + search */}
        <button onClick={goHome} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: GOLD }} data-testid="galacto-home">
          <Orbit size={14} /> Galactopedia{meta?.count ? ` · ${meta.count} topics` : ""}
        </button>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tighter mt-3 leading-[1.03]">The collective brain of the community.</h1>
        <p className="mt-3 text-base max-w-2xl" style={{ color: MUTED }}>
          A living, self-evolving encyclopedia — search verified knowledge, follow topics as they connect, and earn when your contributions make us all smarter.
        </p>

        <form onSubmit={submitSearch} className="mt-6 flex items-center gap-2 max-w-2xl" data-testid="galacto-search-form">
          <div className="flex-1 flex items-center gap-2 rounded-full border px-4 h-12" style={{ borderColor: BORDER, backgroundColor: CARD }}>
            <Search size={18} style={{ color: GOLD }} />
            <input data-testid="galacto-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search any topic — AI, gravity, photosynthesis, history…"
              className="flex-1 bg-transparent outline-none text-sm" style={{ color: TEXT }} />
          </div>
          <button type="submit" data-testid="galacto-search-btn" className="h-12 px-5 rounded-full text-sm font-medium" style={{ backgroundColor: GOLD, color: BG }}>
            Search
          </button>
        </form>

        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {loading && (
              <div className="flex items-center gap-2 text-sm py-10" style={{ color: MUTED }} data-testid="galacto-loading">
                <Loader2 size={16} className="animate-spin" /> Loading…
              </div>
            )}

            {/* Article view */}
            {!loading && entry && (
              <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="galacto-entry">
                <button onClick={goHome} className="inline-flex items-center gap-1.5 text-sm mb-4 hover:opacity-80" style={{ color: MUTED }} data-testid="galacto-back">
                  <ArrowLeft size={15} /> Back to Galactopedia
                </button>
                <div className="rounded-2xl p-6 sm:p-8 border" style={{ backgroundColor: CARD, borderColor: BORDER }}>
                  <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>{entry.category}</span>
                  <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-2 leading-tight" data-testid="galacto-entry-title">{entry.title}</h2>
                  <div className="flex items-center gap-3 mt-2 text-[11px]" style={{ color: MUTED }}>
                    <span className="flex items-center gap-1"><Users size={12} /> {(entry.contributors?.length || 0)} contributor{(entry.contributors?.length || 0) === 1 ? "" : "s"}</span>
                    <span>· {entry.views || 0} views</span>
                    <span className="capitalize">· {entry.origin === "seed" ? "founding topic" : entry.origin === "ai" ? "AI-researched" : "community"}</span>
                  </div>
                  <p className="mt-4 text-base leading-relaxed" style={{ color: TEXT }} data-testid="galacto-entry-summary">
                    {linkify(entry.summary, linkTerms, loadEntry)}
                  </p>
                  {(entry.sections || []).map((s, i) => (
                    <div key={i} className="mt-6" data-testid={`galacto-section-${i}`}>
                      <h3 className="font-serif text-xl" style={{ color: TEXT }}>{s.heading}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed" style={{ color: MUTED }}>{linkify(s.body, linkTerms, loadEntry)}</p>
                    </div>
                  ))}

                  {related.length > 0 && (
                    <div className="mt-8 pt-5 border-t" style={{ borderColor: BORDER }} data-testid="galacto-related">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}><Link2 size={13} /> Related topics</div>
                      <div className="flex flex-wrap gap-2">
                        {related.map((r) => (
                          <button key={r.slug} data-testid={`related-${r.slug}`} onClick={() => loadEntry(r.slug)}
                            className="px-3 py-1.5 rounded-full text-xs border hover:opacity-80 transition-opacity" style={{ borderColor: BORDER, color: TEXT }}>
                            {r.term}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {(entry.sources || []).length > 0 && (
                    <div className="mt-6 pt-5 border-t" style={{ borderColor: BORDER }} data-testid="galacto-sources">
                      <div className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: GOLD }}>Sources</div>
                      <ul className="space-y-1.5">
                        {entry.sources.map((src, i) => (
                          <li key={i}>
                            <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-sm inline-flex items-center gap-1.5 hover:opacity-80" style={{ color: TEXT }}>
                              <ExternalLink size={13} style={{ color: GOLD }} /> {src.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="galacto-share">
                  <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>Share this topic</span>
                  <ShareBar url={shareVec} title={`${entry.title} · My Kind`} text={`${entry.title} — on My Kind Galactopedia`} />
                </div>
              </motion.article>
            )}

            {/* Search results */}
            {!loading && !entry && searched && (
              <div className="space-y-3" data-testid="galacto-results">
                {results.length > 0 ? (
                  results.map((r) => (
                    <button key={r.slug} data-testid={`galacto-result-${r.slug}`} onClick={() => loadEntry(r.slug)}
                      className="w-full text-left rounded-2xl p-5 border hover:opacity-90 transition-opacity group" style={{ backgroundColor: CARD, borderColor: BORDER }}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-serif text-lg" style={{ color: TEXT }}>{r.title}</span>
                        <ArrowRight size={16} style={{ color: GOLD }} className="shrink-0" />
                      </div>
                      <p className="text-sm mt-1 line-clamp-2" style={{ color: MUTED }}>{r.snippet}</p>
                      <div className="text-[11px] mt-2" style={{ color: GOLD }}>{r.category} · {r.views} views</div>
                    </button>
                  ))
                ) : (
                  <div className="rounded-2xl p-8 border text-center" style={{ backgroundColor: CARD, borderColor: BORDER }} data-testid="galacto-no-results">
                    <Sparkles size={26} style={{ color: GOLD }} className="mx-auto mb-3" />
                    <div className="font-serif text-xl">No topic yet for “{query}”.</div>
                    <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: MUTED }}>
                      Let My Kind research it and add it to the collective brain{meta ? ` for ${meta.pricing.generate} credit` : ""}.
                    </p>
                    <button data-testid="galacto-generate-btn" onClick={generate} disabled={generating}
                      className="mt-4 px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 font-medium disabled:opacity-60" style={{ backgroundColor: GOLD, color: BG }}>
                      {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Research & add “{query.slice(0, 32)}”
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Default: browse the wiki (nothing searched yet) */}
            {!loading && !entry && !searched && (
              <BrowseAll meta={meta} onOpen={loadEntry} />
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Contribute onDone={(slug) => { loadNews(); if (slug) loadEntry(slug); }} />
            <Leaderboard />
            <NewsFeed news={news} onOpen={loadEntry} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BrowseAll({ meta, onOpen }) {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  useEffect(() => {
    api.get("/galactopedia/search", { params: { category: cat, limit: 24 } })
      .then(({ data }) => setItems(data.results || [])).catch(() => {});
  }, [cat]);
  return (
    <div data-testid="galacto-browse">
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setCat("")} className="px-3 py-1.5 rounded-full text-xs border" data-testid="galacto-cat-all"
          style={{ borderColor: cat === "" ? GOLD : BORDER, color: cat === "" ? GOLD : MUTED }}>All</button>
        {(meta?.categories || []).map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`galacto-cat-${c}`} className="px-3 py-1.5 rounded-full text-xs border"
            style={{ borderColor: cat === c ? GOLD : BORDER, color: cat === c ? GOLD : MUTED }}>{c}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((r) => (
          <button key={r.slug} data-testid={`galacto-browse-${r.slug}`} onClick={() => onOpen(r.slug)}
            className="text-left rounded-2xl p-5 border hover:opacity-90 transition-opacity" style={{ backgroundColor: CARD, borderColor: BORDER }}>
            <div className="font-serif text-lg" style={{ color: TEXT }}>{r.title}</div>
            <p className="text-sm mt-1 line-clamp-2" style={{ color: MUTED }}>{r.snippet}</p>
            <div className="text-[11px] mt-2" style={{ color: GOLD }}>{r.category}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Leaderboard() {
  const { user } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/galactopedia/leaderboard").then((r) => setData(r.data)).catch(() => {});
  }, [user?.user_id]);
  if (!data || !(data.leaderboard || []).length) return null;
  const Chip = ({ badge }) => badge ? (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0"
      style={{ backgroundColor: `${badge.color}22`, color: badge.color, border: `1px solid ${badge.color}55` }}>
      {badge.name}
    </span>
  ) : null;
  return (
    <div className="rounded-2xl p-5 border" style={{ backgroundColor: CARD, borderColor: BORDER }} data-testid="galacto-leaderboard">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest mb-4" style={{ color: GOLD }}>
        <Users size={14} /> Top Contributors
      </div>
      <div className="space-y-2">
        {data.leaderboard.map((b) => (
          <div key={b.user_id} data-testid={`leaderboard-row-${b.rank}`}
            className="flex items-center gap-3 rounded-xl px-3 py-2"
            style={{ backgroundColor: user && b.user_id === user.user_id ? `${GOLD}14` : "transparent" }}>
            <span className="font-serif text-sm w-5 text-center shrink-0" style={{ color: b.rank <= 3 ? GOLD : MUTED }}>{b.rank}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate" style={{ color: TEXT }}>{b.name}</div>
              <div className="text-[11px]" style={{ color: MUTED }}>{b.articles} article{b.articles === 1 ? "" : "s"}</div>
            </div>
            <Chip badge={b.badge} />
          </div>
        ))}
      </div>
      {data.me && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: BORDER }} data-testid="leaderboard-me">
          {data.me.badge ? (
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px]" style={{ color: MUTED }}>
                You · {data.me.articles} article{data.me.articles === 1 ? "" : "s"}{data.me.rank ? ` · rank #${data.me.rank}` : ""}
              </div>
              <Chip badge={data.me.badge} />
            </div>
          ) : (
            <div className="text-[11px]" style={{ color: MUTED }}>
              Push your first accepted article to earn a <b style={{ color: GOLD }}>Contributor</b> badge.
            </div>
          )}
          {data.me.badge?.next && (
            <div className="text-[10px] mt-1" style={{ color: MUTED }}>
              {data.me.badge.next.at - data.me.articles} more to reach <b style={{ color: GOLD }}>{data.me.badge.next.name}</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

